import { Component, OnDestroy, OnInit } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { finalize, first, takeUntil } from 'rxjs/operators';
import { UserService } from 'src/sb-tools/typescript/services/user.service';
import { UserDataService } from 'src/sb-tools/typescript/services/user-data.service';
import { Subject } from 'rxjs/internal/Subject';
import { BaseUserWithRole } from '@sb-tools/typescript/models/base-user-with-role.model';
import { VendorService } from '../../../core/services/vendor.service';
import { UserSearchType } from '../../../core/enums/user-search-type.enum';
import { InlineSVGComponent } from '../../../../sb-modules/Frontend-Inline-SVG-Module/inline-svg.component';
import { TranslateModule } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';
import { FocusDirective } from '../../../../sb-tools/typescript/directives/focus.directive';
import { TagsInputComponent } from '../../../../sb-modules/Frontend-form-field-module/tags-input/tags-input.component';
import { NewVendorFormComponent } from '../../components/new-vendor-form/new-vendor-form.component';

@Component({
    selector: 'app-select-users-to-assign-modal',
    templateUrl: './select-user-to-assign-modal.component.html',
    styleUrls: ['./select-user-to-assign-modal.component.scss'],
    imports: [
        InlineSVGComponent,
        TranslateModule,
        FormsModule,
        FocusDirective,
        TagsInputComponent,
        NewVendorFormComponent,
    ],
})
export class SelectUsersToAssignModalComponent implements OnInit, OnDestroy {
    comment;
    confirmCallback;
    confirmText: string;
    isAddingNewUsersAllowed: boolean;
    isAddingUser: boolean;
    isAssigning: boolean;
    isShowForm: boolean;
    newUsers = [];
    title: string;
    users = [];
    userSearchType: UserSearchType;

    private unsubscriber = new Subject<void>();

    constructor(
        private modalRef: BsModalRef,
        private userService: UserService,
        private userDataService: UserDataService,
        private vendorService: VendorService,
    ) {}

    ngOnInit() {
        if (this.userSearchType === UserSearchType.team) {
            this.userService
                .getTeammates()
                .pipe(first())
                .subscribe((data: BaseUserWithRole[]) => {
                    return this.processUsers(data);
                });
            this.isAddingNewUsersAllowed = false;
        } else if (this.userSearchType === UserSearchType.vendors) {
            this.userDataService
                .getVendors()
                .pipe(first())
                .subscribe((data: BaseUserWithRole[]) => {
                    return this.processUsers(data);
                });
            this.isAddingNewUsersAllowed = true;
        }
    }

    addNewUser(email, name, shouldBeClosed, callback?) {
        if (
            !this.newUsers.some((selectUser) => {
                return selectUser.email === email;
            })
        ) {
            const existedUser = this.users.find((oldUser) => {
                if (oldUser.email) {
                    return oldUser.email.toLowerCase() === email.toLowerCase();
                } else {
                    return false;
                }
            });
            if (existedUser) {
                if (!this.newUsers) {
                    this.newUsers = [];
                }
                this.newUsers.push(existedUser);
                if (callback && typeof callback === 'function') {
                    callback();
                }

                if (shouldBeClosed) {
                    this.confirmCallback({
                        users: this.newUsers,
                        message: this.comment,
                    });
                    this.modalRef.hide();
                }
            } else {
                this.isAddingUser = true;
                if (!name) {
                    name = email.split('@')[0];
                }

                this.vendorService
                    .add({
                        name,
                        email,
                    })
                    .pipe(
                        finalize(() => {
                            this.isAddingUser = false;
                        }),
                        takeUntil(this.unsubscriber),
                    )
                    .subscribe((data: string) => {
                        if (!this.newUsers) {
                            this.newUsers = [];
                        }
                        this.newUsers.push({
                            name,
                            email,
                            id: data,
                            label: name ? name + ' ' + email : email,
                        });
                        this.users.push({
                            name,
                            email,
                            id: data,
                            label: name ? name + ' ' + email : email,
                        });

                        this.userService.reloadData();

                        if (callback && typeof callback === 'function') {
                            callback();
                        }
                        if (shouldBeClosed) {
                            this.confirmCallback({
                                users: this.newUsers,
                                message: this.comment,
                            });
                            this.modalRef.hide();
                        }
                    });
            }
        } else {
            if (callback && typeof callback === 'function') {
                callback();
            }
        }
    }

    confirm(): void {
        this.isAssigning = true;
        this.confirmCallback({
            users: this.newUsers,
            message: this.comment,
        });
        this.modalRef.hide();
    }

    processUsers(users: BaseUserWithRole[]): void {
        users.forEach((item) => {
            item.label = item.name ? item.name + ' ' + item.email : item.email;
        });
        this.users = users;
    }

    decline(): void {
        this.modalRef.hide();
    }

    ngOnDestroy() {
        this.unsubscriber.next();
        this.unsubscriber.complete();
    }
}
